import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { GuardianPlayerSession } from '@/features/guardian-player/services';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import React, { memo } from 'react';
import { Pressable } from 'react-native';
import type { SessionHistoryListDataItem } from './utils';

type Props = {
  item: SessionHistoryListDataItem;
  onOpenSessionDetail: (session: GuardianPlayerSession) => void;
  selected?: boolean;
};

const strings = STRINGS.guardian.playerSessionHistories;

const SessionHistoryListItem = ({ item, onOpenSessionDetail, selected = false }: Props) => {
  const styles = useStyles();
  const { scale } = useScaling();

  if (item.kind === 'month') {
    return (
      <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.monthTitle}>
        {item.label}
      </Text>
    );
  }

  const { dateParts, session } = item;
  const pinLabel = session.pinCount === 1 ? strings.pinSingular : strings.pinPlural;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${session.type}, ${dateParts.weekday} ${dateParts.day}, ${session.coachName}`}
      accessibilityHint={strings.sessionDetailTitle}
      onPress={() => onOpenSessionDetail(session)}
      style={[styles.sessionCard, selected ? styles.selectedSessionCard : null]}>
      <Stack direction="row" align="center" gap={scale(10)}>
        <Stack align="center" justify="center" style={styles.dateCard}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 11 Regular" style={styles.weekday}>
            {dateParts.weekday}
          </Text>
          <Text
            fontFamily={fontFamilies.montserrat}
            fontSize={scale(20)}
            lineHeight={scale(28)}
            fontWeight={'700'}
            style={styles.day}>
            {dateParts.day}
          </Text>
        </Stack>

        <Stack flex={1}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
            numberOfLines={1}
            style={styles.sessionType}>
            {session.type}
          </Text>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            numberOfLines={1}
            style={styles.coachName}>
            {session.coachName}
          </Text>
        </Stack>

        {session.pinCount > 0 ? (
          <Stack style={styles.pinBadge}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
              numberOfLines={1}
              style={styles.pinText}>
              {strings.pinBadge
                .replace('{pins}', String(session.pinCount))
                .replace('{label}', pinLabel)}
            </Text>
          </Stack>
        ) : null}
      </Stack>
    </Pressable>
  );
};

const useStyles = createStyles(({ scale, isTablet }) => ({
  monthTitle: {
    color: guardianColors['10'],
    paddingTop: scale(16),
    paddingBottom: scale(8),
  },
  sessionCard: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    marginBottom: scale(8),
    borderWidth: scale(1),
    borderColor: colors['0'],
  },
  selectedSessionCard: {
    backgroundColor: guardianColors['Main/10'],
    borderColor: guardianColors.Blue,
  },
  dateCard: {
    width: scale(50),
    height: scale(50),
    borderRadius: scale(8),
    borderCurve: 'continuous',
    backgroundColor: guardianColors['8'],
  },
  weekday: {
    color: guardianColors['6'],
  },
  day: {
    color: guardianColors['Main/Main'],
  },
  sessionType: {
    color: guardianColors['Main/Main'],
  },
  coachName: {
    color: guardianColors['10'],
  },
  pinBadge: {
    flexShrink: 0,
    borderRadius: scale(30),
    borderCurve: 'continuous',
    backgroundColor: isTablet ? colors['0'] : guardianColors['Main/10'],
    paddingHorizontal: scale(10),
    paddingVertical: scale(4),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 1,
    elevation: 1,
  },
  pinText: {
    color: guardianColors['Main/Main'],
  },
}));

export default memo(SessionHistoryListItem);
